import { useState, useCallback, ChangeEvent, useMemo } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { v4 as uuidv4 } from 'uuid';
import { Address } from '@/types/Address';
import { useQuery } from '@tanstack/react-query';
import { getAddress, getPerimeterList } from '@/api';
import { AddressResponse } from '@/types/ApiTypes';
import { useTranslation } from 'react-i18next';
import { useGtm } from '@/hooks/useGtm';

function useAddressSearch() {
  const { t } = useTranslation('landing-page');
  const { pushToDataLayer } = useGtm();

  const DEBOUNCE = 300;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const debouncedSearchTerm = useDebounce(searchQuery, DEBOUNCE);

  const { data: searchOptions = [], isLoading: isAddressLoading } = useQuery({
    queryKey: ['address', debouncedSearchTerm],
    queryFn: () => getAddress(debouncedSearchTerm),
    enabled: !!debouncedSearchTerm
  });

  const { data: perimeterList = [], isLoading: isPerimeterLoading } = useQuery({
    queryKey: ['perimeterList'],
    queryFn: () => getPerimeterList()
  });

  const handleInputChange = (
    _event: ChangeEvent<unknown>,
    newInputValue: string,
    reason: string
  ) => {
    const update = (newTextValue: string) => {
      setSearchQuery(newTextValue);
      setSelectedAddress(null);
    };
    if (reason === 'input') {
      if (newInputValue.length === 0) {
        update('');
        return;
      }
      setSearchQuery(newInputValue);
    }
    else if (reason === 'clear') {
      update('');
    }
    else if (reason === 'reset') {
      update(newInputValue);
    }
  };

  const handleOptionSelect = useCallback((_event: any, newValue: any) => {
    if (newValue && newValue.id) {
      setSearchQuery(newValue.label);
      setSelectedAddress(newValue);

      pushToDataLayer({
        event: 'address_input',
        addressEntered: newValue.label,
        stepName: 'AddressSearch'
      });
    }
    else {
      setSelectedAddress(null);
    }
  }, []);

  const enhancedSearchOptions = useMemo<AddressResponse[]>(() => {
    if (!isAddressLoading && searchQuery.length > 0 && searchOptions.length === 0) {
      return [
        {
          id: uuidv4(),
          street: '',
          houseNumber: '',
          zip: '',
          town: '',
          lat: 0,
          lon: 0,
          label: t('hero.address-search.address-not-found'),
          group: 'address',
          isCustom: true
        }
      ];
    }
    return searchOptions;
  }, [isAddressLoading, searchQuery, searchOptions, t]);

  const enhancedPerimeterList = useMemo(() => {
    const perList = perimeterList.filter((perimeter) => {
      return (
        perimeter.description?.Typ !== 'On-Hold' &&
        perimeter.description?.Typ !== 'Entwicklungsgebiet'
      );
    });

    if (searchQuery.length === 0) {
      return perList;
    }

    if (!isPerimeterLoading) {
      const filteredPerimeterList = perList.filter((perimeter) =>
        perimeter.label.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (filteredPerimeterList.length === 0 && searchQuery.length > 0) {
        return [
          {
            id: uuidv4(),
            label: t('hero.address-search.perimeter-not-found').replace('{query}', searchQuery),
            lat: 0,
            long: 0,
            group: 'perimeter',
            isCustom: true
          }
        ];
      }

      return filteredPerimeterList;
    }
    return [];
  }, [isPerimeterLoading, perimeterList, searchQuery, t]);

  return {
    searchQuery,
    setSearchQuery,
    searchOptions,
    perimeterList,
    enhancedSearchOptions,
    enhancedPerimeterList,
    isLoading: isAddressLoading || isPerimeterLoading,
    handleInputChange,
    handleOptionSelect,
    selectedAddress
  };
}

export default useAddressSearch;
